import { useCallback, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { showApiError, showError, showSuccess } from '@/lib/utils/notifications'
import { sendAnalytics } from '@/lib/utils/analytics'
import type { EmailTemplateDetail, WorkflowDetail, WorkflowOption } from '../types/emailTemplate'
import {
  getCbActiveConfig,
  getEmailTemplateById,
  getWorkflowDetail,
  getWorkflows,
  saveEmailTemplate,
} from '../services/emailTemplatesService'
import {
  emptyEmailTemplateForm,
  formToApiPayload,
  getTemplateFieldError,
  mapDetailToForm,
  needsTemplateMetadata,
  type EmailTemplateFormState,
  type TemplateFormValidationContext,
  validateTemplateForm,
} from '../utils/emailTemplateFormMap'
import { normalizeWorkflowVariables } from '../utils/normalizeWorkflowVariables'

type Params = { templateId?: string }

export function useEmailTemplateAddEditPage() {
  const navigate = useNavigate()
  const { templateId: templateIdParam } = useParams<Params>()
  const templateId =
    templateIdParam && /^\d+$/.test(templateIdParam)
      ? Number.parseInt(templateIdParam, 10)
      : undefined
  const isEdit = templateId != null && Number.isFinite(templateId) && templateId > 0

  const [workflows, setWorkflows] = useState<WorkflowOption[]>([])
  const [loadingList, setLoadingList] = useState(true)
  const [loadingDetail, setLoadingDetail] = useState(false)
  const [saving, setSaving] = useState(false)
  const [templateDetail, setTemplateDetail] = useState<EmailTemplateDetail | null>(null)
  const [addWorkflowDetail, setAddWorkflowDetail] = useState<WorkflowDetail | null>(null)
  const [cbConfig, setCbConfig] = useState<{ active?: string; details?: Record<string, unknown> } | null>(
    null,
  )
  const [reasonOpen, setReasonOpen] = useState(false)
  const [isPreview, setIsPreview] = useState(false)
  const [testMailOpen, setTestMailOpen] = useState(false)
  const [testMailType, setTestMailType] = useState<'Email' | 'SMS'>('Email')
  const [testMailCtx, setTestMailCtx] = useState<{
    workflowType: string
    availableVariables: Record<string, string> | null
    emailBody: string
    smsBody: string
  } | null>(null)

  const form = useForm<EmailTemplateFormState>({
    defaultValues: emptyEmailTemplateForm,
    mode: 'onTouched',
  })

  const {
    control,
    register,
    handleSubmit,
    watch,
    reset,
    getValues,
    setError,
    clearErrors,
    setValue,
    formState: { errors, isDirty, touchedFields },
  } = form

  const workflowIdWatch = watch('fk_workflow_id')
  const passthruMailgun = watch('passthru_mailgun')

  const loadWorkflows = useCallback(async () => {
    setLoadingList(true)
    try {
      const w = await getWorkflows()
      setWorkflows(Array.isArray(w) ? w : [])
    } catch (e) {
      showApiError(e)
    } finally {
      setLoadingList(false)
    }
  }, [])

  useEffect(() => {
    void loadWorkflows()
  }, [loadWorkflows])

  useEffect(() => {
    const w = workflowIdWatch
    if (!w) {
      setAddWorkflowDetail(null)
      return
    }
    const id = Number.parseInt(String(w), 10)
    if (Number.isNaN(id) || id < 1) return
    let cancelled = false
    void (async () => {
      try {
        const detail = await getWorkflowDetail(id)
        if (!cancelled) setAddWorkflowDetail(detail)
      } catch (e) {
        if (!cancelled) {
          showApiError(e)
          setAddWorkflowDetail(null)
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [workflowIdWatch])

  useEffect(() => {
    if (!isEdit || !templateId) return
    setLoadingDetail(true)
    void (async () => {
      try {
        const detail = await getEmailTemplateById(templateId)
        setTemplateDetail(detail)
        clearErrors()
        reset(mapDetailToForm(detail), { keepDirty: false, keepTouched: false })
        if (detail.fk_workflow_id) {
          try {
            setAddWorkflowDetail(await getWorkflowDetail(detail.fk_workflow_id))
          } catch {
            setAddWorkflowDetail(null)
          }
        }
        if (detail.workflow_type === 'DENIED_APPLICATION') {
          try {
            setCbConfig(await getCbActiveConfig())
          } catch {
            setCbConfig(null)
          }
        } else {
          setCbConfig(null)
        }
      } catch (e) {
        showApiError(e)
        navigate('/email-templates', { replace: true })
      } finally {
        setLoadingDetail(false)
      }
    })()
  }, [isEdit, templateId, reset, clearErrors, navigate])

  const selectedWorkflowType =
    addWorkflowDetail?.workflow_type ||
    workflows.find((w) => String(w.workflow_id) === String(workflowIdWatch))?.workflow_type ||
    templateDetail?.workflow_type
  const workflowType = selectedWorkflowType
  const isNotification = templateDetail?.is_notification
  const isSms = templateDetail?.is_sms
  const showPassthru =
    typeof window !== 'undefined' && window.location.hostname !== 'cornerstone.libertyrent.com'
  const showMeta = !!workflowType && needsTemplateMetadata(workflowType)

  const validationContext = useMemo<TemplateFormValidationContext>(() => {
    return {
      workflowType,
      isEdit: !!isEdit,
      isNotification: isEdit ? isNotification : undefined,
      isSms: isEdit ? isSms : undefined,
      requirePassthru: showPassthru,
    }
  }, [isEdit, workflowType, isNotification, isSms, showPassthru])

  const validateFieldOnBlur = useCallback(
    (field: keyof EmailTemplateFormState) => {
      const msg = getTemplateFieldError(field, getValues(), validationContext)
      if (msg) setError(field, { type: 'manual', message: msg })
      else clearErrors(field)
    },
    [getValues, validationContext, setError, clearErrors],
  )

  const registerField = useCallback(
    <K extends keyof EmailTemplateFormState>(name: K) => {
      const registration = register(name)
      return {
        ...registration,
        onChange: (event: Parameters<typeof registration.onChange>[0]) => {
          clearErrors(name)
          return registration.onChange(event)
        },
        onBlur: async (event: Parameters<typeof registration.onBlur>[0]) => {
          await registration.onBlur(event)
          validateFieldOnBlur(name)
        },
      }
    },
    [register, clearErrors, validateFieldOnBlur],
  )

  const showFieldError = (field: keyof EmailTemplateFormState): boolean =>
    Boolean(errors[field] && touchedFields[field])

  const fieldErrorMessage = (field: keyof EmailTemplateFormState): string | undefined =>
    errors[field]?.message

  const executeSave = async (changeReason?: string) => {
    const values = getValues()
    const payload = formToApiPayload(values, workflowType, templateDetail) as Record<string, unknown>
    if (changeReason) payload.change_reason = changeReason

    setSaving(true)
    try {
      await saveEmailTemplate(payload, isEdit ? templateId : undefined)
      sendAnalytics('click', 'template edit', '')
      showSuccess(
        isEdit ? 'Template updated successfully!' : 'Template added successfully!',
      )
      navigate('/email-templates')
    } catch (e) {
      showApiError(e)
    } finally {
      setSaving(false)
    }
  }

  const onFormValid = (values: EmailTemplateFormState) => {
    const { summary, fieldErrors } = validateTemplateForm(values, validationContext)
    const invalidKeys = Object.entries(fieldErrors).filter(([, msg]) => msg) as [
      keyof EmailTemplateFormState,
      string,
    ][]
    if (invalidKeys.length > 0) {
      for (const [k, msg] of invalidKeys) {
        setError(k, { type: 'manual', message: msg })
        setValue(k, values[k], { shouldTouch: true })
      }
      showError(summary ?? 'Please fix validation errors before saving.', false)
      return
    }
    if (isDirty) {
      setReasonOpen(true)
    } else {
      void executeSave()
    }
  }

  const handleReasonSubmit = async (reason: string) => {
    setReasonOpen(false)
    await executeSave(reason)
  }

  const openSendTestMail = (type: 'Email' | 'SMS') => {
    const wfId = getValues('fk_workflow_id')
    if (!wfId) {
      showError('Please select a workflow first.', false)
      return
    }
    const wfType =
      addWorkflowDetail?.workflow_type ||
      workflows.find((w) => String(w.workflow_id) === String(wfId))?.workflow_type ||
      templateDetail?.workflow_type
    if (!wfType) {
      showError('Could not resolve workflow. Try again after the workflow loads.', false)
      return
    }
    setTestMailCtx({
      workflowType: wfType,
      availableVariables: addWorkflowDetail?.available_variables ?? null,
      emailBody: getValues('email_body') || '',
      smsBody: getValues('sms_body') || '',
    })
    setTestMailType(type)
    setTestMailOpen(true)
  }

  const closeTestMail = () => {
    setTestMailOpen(false)
    setTestMailCtx(null)
  }

  const varSource = (addWorkflowDetail || {}) as WorkflowDetail
  const recipientVars = normalizeWorkflowVariables(varSource.recipient_variables)
  const availableVars = normalizeWorkflowVariables(varSource.available_variables)
  const wt = workflowType

  return {
    isEdit,
    loadingList,
    loadingDetail,
    saving,
    templateDetail,
    workflows,
    cbConfig,
    reasonOpen,
    setReasonOpen,
    isPreview,
    setIsPreview,
    testMailOpen,
    testMailType,
    testMailCtx,
    control,
    register,
    handleSubmit,
    watch,
    passthruMailgun,
    errors,
    isNotification,
    isSms,
    showPassthru,
    showMeta,
    workflowType: wt,
    recipientVars,
    availableVars,
    varSource,
    registerField,
    showFieldError,
    fieldErrorMessage,
    validateFieldOnBlur,
    clearErrors,
    setValue,
    onFormValid,
    handleReasonSubmit,
    openSendTestMail,
    closeTestMail,
    navigate,
  }
}

export type EmailTemplateAddEditPageState = ReturnType<typeof useEmailTemplateAddEditPage>
